import { computed, onScopeDispose, ref, shallowRef, watch, type Ref } from "vue";
import type { PaginationResult } from "convex/server";
import { getProjectsConvexClient, projectFns, recoverProjectsConvexSubscription } from "../convexClient";
import { captureConvexError } from "../telemetry";
import type { ChatMessage } from "../types";

/** Reactive pages with fixed end cursors keep already loaded history as new messages arrive. */
export function useThreadMessages(threadId: Ref<string | null>) {
  const pages = shallowRef<PaginationResult<ChatMessage>[]>([]);
  const isLoadingMore = ref(false);
  const loadError = ref<string | null>(null);
  let generation = 0;
  const unsubscribers: (() => void)[] = [];
  let recoveryAttempt = 0;
  let pendingPage: number | null = null;
  const messages = computed(() => {
    const unique = new Map(pages.value.flatMap(page => page.page).map(message => [message._id, message]));
    return [...unique.values()].reverse();
  });

  function subscribePage(index: number, cursor: string | null, endCursor?: string) {
    const id = threadId.value;
    const current = generation;
    if (!id) return;
    const stop = getProjectsConvexClient().onUpdate(projectFns.getThreadMessagesById, {
      thread_id: id as never,
      paginationOpts: { numItems: 50, cursor, ...(endCursor ? { endCursor } : {}) },
    }, result => {
      if (current !== generation) return;
      pages.value = [...pages.value.slice(0, index), result, ...pages.value.slice(index + 1)];
      if (pendingPage === index) { pendingPage = null; isLoadingMore.value = false; }
      loadError.value = null;
    }, error => {
      if (current !== generation) return;
      void recoverProjectsConvexSubscription({ operation: "chat.messages", error, recoveryAttempt }).then(result => {
        if (current !== generation) return;
        recoveryAttempt = result.nextRecoveryAttempt;
        if (result.didRecover) refresh(false);
        else {
          isLoadingMore.value = false;
          loadError.value = "Unable to load messages. Please try again.";
          captureConvexError(error, { operation: "chat.messages_failed", threadId: id, level: "error" });
        }
      });
    });
    unsubscribers[index] = stop;
  }

  function clear() {
    generation++;
    unsubscribers.splice(0).forEach(stop => stop());
  }
  function refresh(resetRecovery = true) {
    clear();
    pages.value = [];
    loadError.value = null;
    isLoadingMore.value = false;
    pendingPage = null;
    if (resetRecovery) recoveryAttempt = 0;
    subscribePage(0, null);
  }
  function loadMore() {
    const last = pages.value[pages.value.length - 1];
    if (!last || last.isDone || isLoadingMore.value) return;
    isLoadingMore.value = true;
    const index = pages.value.length - 1;
    pendingPage = index + 1;
    // Pin the existing page before subscribing after its boundary.
    unsubscribers[index]?.();
    subscribePage(index, index ? pages.value[index - 1]!.continueCursor : null, last.continueCursor);
    subscribePage(index + 1, last.continueCursor);
  }
  watch(threadId, () => refresh(), { immediate: true });
  onScopeDispose(clear);
  return {
    messages, isReady: computed(() => pages.value.length > 0 || !!loadError.value),
    isLoadingMore, loadError, hasMore: computed(() => !!pages.value.length && !pages.value[pages.value.length - 1]!.isDone),
    loadMore, refresh: () => refresh(),
  };
}
